import { useState } from 'react';
import { useNavigate } from 'react-router';
import { SemCadastro } from '../components/SemCadastro.js';
import {
  Aguardar,
  Botao,
  CabecalhoPagina,
  Campo,
  classeCampo,
  classeTd,
  classeTh,
  Etiqueta,
  Painel,
  Tabela,
} from '../components/ui/index.js';
import { formatarData, hojeIso, ROTULO_SEXO } from '../dados/catalogo.js';
import { useConsulta, useFonte, usePodeCadastrar } from '../dados/contexto.js';
import { baixarArquivo } from '../dados/exportar.js';
import { modeloDePlanilha, ordemDeGravacao, prepararImportacao, type LinhaImportada } from '../dados/importacao.js';

/**
 * Importacao de animais por planilha: o plantel inteiro do haras de uma vez, no
 * lugar de 40 formularios. Nada e gravado antes da conferencia: a tela mostra,
 * linha a linha, o que entra, o que ja existe e o que tem erro.
 */
export function ImportarAnimais() {
  const fonte = useFonte();
  const equipe = usePodeCadastrar();
  const navegar = useNavigate();
  const consulta = useConsulta(async (f) => ({ haras: await f.haras(), animais: await f.listarAnimais() }), []);
  const [linhas, setLinhas] = useState<LinhaImportada[] | null>(null);
  const [arquivo, setArquivo] = useState('');
  const [erro, setErro] = useState<string | null>(null);
  const [gravando, setGravando] = useState(false);

  if (!fonte.cadastro || !equipe) return <SemCadastro demo={fonte.ficticia} />;

  return (
    <Aguardar consulta={consulta}>
      {({ haras, animais }) => {
        if (!haras) return <p className="text-sm text-apagado">Cadastre um haras antes de importar animais.</p>;

        const ler = async (f: File | undefined) => {
          setErro(null);
          setLinhas(null);
          if (!f) return;
          setArquivo(f.name);
          try {
            const r = prepararImportacao(await f.text(), { existentes: animais, harasId: haras.id, hoje: hojeIso() });
            if (r.length === 0) setErro('A planilha está vazia ou sem cabeçalho. Baixe o modelo para ver o formato.');
            setLinhas(r);
          } catch (e) {
            setErro(e instanceof Error ? e.message : String(e));
          }
        };

        const novas = linhas?.filter((l) => l.situacao === 'nova') ?? [];
        const importar = async () => {
          setGravando(true);
          try {
            for (const l of ordemDeGravacao(linhas ?? [])) await fonte.cadastro!.salvarAnimal(l.animal);
            navegar('/animais', { replace: true });
          } catch (e) {
            setErro(e instanceof Error ? e.message : String(e));
          } finally {
            setGravando(false);
          }
        };

        return (
          <div className="space-y-6">
            <CabecalhoPagina
              trilha={[{ rotulo: 'Plantel', para: '/animais' }, { rotulo: 'Importar planilha' }]}
              sobretitulo={haras.name}
              titulo="Importar animais"
              meta={<span>Uma planilha CSV (Excel ou Google Planilhas), uma linha por animal.</span>}
              acoes={<Botao onClick={() => baixarArquivo('equiview-modelo-animais.csv', modeloDePlanilha())}>Baixar modelo</Botao>}
            />

            <Painel titulo="Planilha">
              <div className="space-y-3">
                <Campo
                  rotulo="Arquivo CSV"
                  ajuda="Colunas: nome, registro, sexo, nascimento (dd/mm/aaaa), pelagem, marcha, pai, mae (pelo registro), microchip, observacoes. No Excel: Salvar como → CSV."
                >
                  {(a) => (
                    <input {...a} type="file" accept=".csv,text/csv" onChange={(e) => void ler(e.target.files?.[0])} className={classeCampo()} />
                  )}
                </Campo>
                {erro && <p className="text-sm text-erro">{erro}</p>}
              </div>
            </Painel>

            {linhas && linhas.length > 0 && (
              <Painel
                titulo={`${arquivo} · ${linhas.length} linha(s)`}
                corpo="p-0"
                acoes={
                  <Botao variante="principal" disabled={novas.length === 0} carregando={gravando} onClick={() => void importar()}>
                    Importar {novas.length} animal(is)
                  </Botao>
                }
              >
                <Tabela rotulo="Conferência da planilha">
                  <thead>
                    <tr>
                      <th scope="col" className={`${classeTh} text-right`}>Linha</th>
                      <th scope="col" className={classeTh}>Animal</th>
                      <th scope="col" className={`${classeTh} hidden sm:table-cell`}>Sexo · nascimento</th>
                      <th scope="col" className={classeTh}>Situação</th>
                    </tr>
                  </thead>
                  <tbody>
                    {linhas.map((l) => (
                      <tr key={l.linha}>
                        <td className={`${classeTd} numero text-right text-apagado`}>{l.linha}</td>
                        <td className={classeTd}>
                          <span className="font-medium">{l.nome || '—'}</span>
                          {l.animal.abccmm_registry && <span className="block font-mono text-xs text-apagado">{l.animal.abccmm_registry}</span>}
                        </td>
                        <td className={`${classeTd} hidden text-apagado sm:table-cell`}>
                          {ROTULO_SEXO[l.animal.sex]} · {l.animal.birth_date ? formatarData(l.animal.birth_date) : '—'}
                        </td>
                        <td className={classeTd}>
                          {l.situacao === 'nova' && <Etiqueta tom="ok">Entra</Etiqueta>}
                          {l.situacao === 'existe' && <Etiqueta>Já cadastrado</Etiqueta>}
                          {l.situacao === 'erro' && <Etiqueta tom="aviso">Com erro</Etiqueta>}
                          {[...l.erros, ...l.avisos].map((m) => (
                            <span key={m} className={`block text-xs ${l.erros.includes(m) ? 'text-erro' : 'text-apagado'}`}>
                              {m}
                            </span>
                          ))}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </Tabela>
                <p className="border-t border-borda px-4 py-3 text-xs text-apagado">
                  Só entram as linhas marcadas "Entra". Corrija as outras na planilha e importe de novo: o que já foi importado
                  aparece como "Já cadastrado" e não duplica.
                </p>
              </Painel>
            )}
          </div>
        );
      }}
    </Aguardar>
  );
}
